import React, { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", category: "Solar Cost",
  cover_image: "", author: "Orax Solar Team", tags: [],
  meta_title: "", meta_description: "", published: true,
};
const CATEGORIES = ["Solar Cost", "Subsidy", "Maintenance", "Comparison"];

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {obj}=edit
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blogs").then(({ data }) => setBlogs(data));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY, tags: [] });
  const openEdit = (b) => setEditing({ ...b, tags: b.tags || [] });
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...editing };
      if (typeof body.tags === "string") body.tags = body.tags.split(",").map(t => t.trim()).filter(Boolean);
      if (body.id) {
        await api.put(`/admin/blogs/${body.id}`, body);
        toast.success("Blog updated");
      } else {
        await api.post("/admin/blogs", body);
        toast.success("Blog published");
      }
      close();
      load();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    await api.delete(`/admin/blogs/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-blogs">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="h2 text-[#0A1F15]">Blog Posts</h1>
          <p className="text-[#5C7368]">{blogs.length} articles</p>
        </div>
        <button onClick={openNew} className="btn-primary !py-2.5 text-sm" data-testid="new-blog-btn">
          <Plus weight="bold" size={16}/> New Post
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E3DB] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-left text-[#5C7368] uppercase text-xs tracking-wider">
            <tr>
              <th className="px-5 py-3">Title</th>
              <th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Updated</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {blogs.map(b => (
              <tr key={b.id} className="border-t border-[#E5E3DB] hover:bg-[#F9F8F6]">
                <td className="px-5 py-3 font-semibold text-[#0A1F15] max-w-md">{b.title}</td>
                <td className="px-5 py-3">{b.category}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-md ${b.published ? "bg-[#135934]/10 text-[#135934]" : "bg-[#F0EFEB] text-[#5C7368]"}`}>
                    {b.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-5 py-3 text-[#5C7368] whitespace-nowrap">{new Date(b.updated_at || b.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(b)} className="p-1.5 rounded hover:bg-[#F0EFEB]" data-testid={`edit-blog-${b.id}`}><PencilSimple size={16}/></button>
                    <button onClick={() => remove(b.id)} className="p-1.5 rounded text-red-600 hover:bg-red-50"><Trash size={16}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-form">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E3DB] sticky top-0 bg-white z-10">
              <h2 className="h3">{editing.id ? "Edit blog" : "New blog post"}</h2>
              <button type="button" onClick={close} className="p-2 rounded hover:bg-[#F0EFEB]"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Title">
                <input required value={editing.title} onChange={e => setEditing({...editing, title:e.target.value})} className="input w-full px-4 py-3 rounded-xl border border-[#E5E3DB]"/>
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Slug (auto if empty)"><input value={editing.slug} onChange={e => setEditing({...editing, slug:e.target.value})} className="input w-full px-4 py-3 rounded-xl border border-[#E5E3DB]"/></Field>
                <Field label="Category">
                  <select value={editing.category} onChange={e => setEditing({...editing, category:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E5E3DB] bg-white">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Cover Image URL"><input value={editing.cover_image} onChange={e => setEditing({...editing, cover_image:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E5E3DB]"/></Field>
              <Field label="Excerpt"><textarea required rows={2} value={editing.excerpt} onChange={e => setEditing({...editing, excerpt:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E5E3DB] resize-none"/></Field>
              <Field label="Content (Markdown supported)"><textarea required rows={12} value={editing.content} onChange={e => setEditing({...editing, content:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E5E3DB] font-mono text-sm resize-y"/></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Meta Title"><input value={editing.meta_title || ""} onChange={e => setEditing({...editing, meta_title:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E5E3DB]"/></Field>
                <Field label="Tags (comma separated)"><input value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={e => setEditing({...editing, tags:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E5E3DB]"/></Field>
              </div>
              <Field label="Meta Description"><textarea rows={2} value={editing.meta_description || ""} onChange={e => setEditing({...editing, meta_description:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-[#E5E3DB] resize-none"/></Field>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editing.published} onChange={e => setEditing({...editing, published:e.target.checked})}/>
                <span>Published</span>
              </label>
            </div>
            <div className="p-5 border-t border-[#E5E3DB] flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={close} className="btn-outline-dark !py-2.5 text-sm">Cancel</button>
              <button disabled={saving} className="btn-primary !py-2.5 text-sm disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-[#5C7368] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
